import { LocalDate, Month } from "@js-joda/core";
import currency from "currency.js";
import h from "trader-hyperscript";
import { Form, checkBox, constantField } from "trader.ts/ui.forms";
import * as style from "../../../styles/style.css.classes";
import {
  checkboxesField,
  checkboxesFieldRenderMultiselectWithPopup,
} from "../../client/checkboxesfield";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import { isEqual } from "../../lib/isequal";
import {
  ddMMyyyy,
  renderCurrency,
  renderFirstNameLastName,
} from "../../render";
import * as routes from "../../routes";
import {
  renderPaymentMethod,
  renderPeriod,
  translateMonth,
} from "../../translations_specific";
import {
  renderReportResult,
  runQueryOnFormChange,
} from "../finance_invoices/client";

export function render(opts: {
  months: [number, Month][];
}): HTMLElement | HTMLElement[] {
  const today = LocalDate.now();
  const initialMonth =
    // This month or first future month
    opts.months
      .filter(
        (m) =>
          (isEqual(m[1], today.month()) && m[0] === today.year()) ||
          LocalDate.from(today).withDayOfMonth(1).isAfter(today),
      )
      .first() ||
    opts.months.last() ||
    null;
  if (!initialMonth) {
    throw new Error("Not months");
  }

  const initialShowAllMonths = opts.months.length > 20 ? false : true;

  const form = new Form({})
    .addField("showAllMonths", [], () =>
      initialShowAllMonths === true
        ? constantField(true, { render: [] })
        : checkBox({
            initialVal: initialShowAllMonths,
            label: "Toon meer maanden",
          }),
    )
    .addField("months", ["showAllMonths"], async ({ showAllMonths }) => {
      const orderedMonths = opts.months.orderBy(
        [(m) => m[0], (m) => m[1].ordinal()],
        ["desc", "desc"],
      );
      const initialMonthIndex = orderedMonths.indexOf(initialMonth);
      return checkboxesField({
        initial: [initialMonth],
        values: orderedMonths.take(
          showAllMonths ? 1000 : initialMonthIndex + 12,
        ),
        show: (s) => translateMonth(s[1]) + " " + s[0],
        addNoFilterOption: false,
        render: checkboxesFieldRenderMultiselectWithPopup,
        label: "Aangemaakt in",
      });
    })
    .build({
      render: (fields) => (
        <section
          className={style.equal_columns + style.cancel_main_margins}
          style="margin-top: -32px"
        >
          <div>
            {fields.months()}
            <div style="text-align: right;">{fields.showAllMonths()}</div>
          </div>
          <div>{/* spacer */}</div>
        </section>
      ),
    });

  const res = runQueryOnFormChange(
    form.s,
    routes.queryPaymentStatisticsSpec,
    (t) => t,
  );

  const id = "classy-table-id";

  return (
    <main style="max-width: unset;">
      <section
        className={style.cancel_main_margins}
        style="background-color: var(--grey-200); padding-bottom: 32px;"
      >
        {"Hier krijg je een overzicht van alle betalingen geregistreerd in Classy, met gekoppelde facturen"}
      </section>

      {form.render()}

      {renderReportResult(res, (rows, _query) => {
        const downloadLinks = renderTableDownloadLinks({
          tableid: id,
          filename: "OverzichtBetalingen",
        });
        const headerRow = (
          <tr>
            <th>{"Datum betaling"}</th>
            <th>{"Factuur"}</th>
            <th style="display: none">{"Factuurdatum"}</th>
            <th style="display: none">{"Schooljaar"}</th>
            <th>{"Student"}</th>
            <th>{"Middel"}</th>
            <th>{"Bedrag"}</th>
          </tr>
        );
        const renderedRows =
          rows === null
            ? [<tr></tr>]
            : rows.map((row) => {
                return (
                  <tr>
                    <td>{row.paymentDate.format(ddMMyyyy)}</td>
                    <td>
                      <a
                        href={routes.invoicedetailsRoute.link({
                          edit: false,
                          invoiceid: row.invoiceid,
                        })}
                      >
                        {row.invoiceexternalid}
                      </a>
                    </td>
                    <td style="display: none">
                      {row.invoiceCreatedDate.format(ddMMyyyy)}
                    </td>
                    <td style="display: none">
                      {renderPeriod(row.period.firstday, row.period.lastday)}
                    </td>
                    <td>{renderFirstNameLastName(row.student)}</td>
                    <td>
                      <div>{renderPaymentMethod(row.method)}</div>
                      {row.paymentproviderid ? (
                        <div>
                          <a
                            href={routes.payment_detailRoute.link({
                              paymentproviderid: row.paymentproviderid,
                            })}
                          >
                            {row.paymentproviderid}
                          </a>
                        </div>
                      ) : (
                        []
                      )}
                    </td>
                    <td
                      style={`background-color: ${
                        row.amount.value > 0 ? "#b9f5a7" : "#ffb48e"
                      }`}
                    >
                      {renderCurrency(row.amount)}
                    </td>
                  </tr>
                );
              });
        const renderedTotal =
          rows === null ? (
            <span></span>
          ) : (
            <tr style="font-weight: bold;background-color: var(--grey-100);">
              <td>{"Totaal" + ": " + rows.length}</td>
              <td></td>
              <td></td>
              <td style="display: none"></td>
              <td style="display: none"></td>
              <td></td>
              <td>
                {renderCurrency(
                  rows.reduce((acc, row) => acc.add(row.amount), currency(0)),
                )}
              </td>
            </tr>
          );

        return (
          <div>
            {downloadLinks}
            <table id={id}>
              {headerRow}
              {renderedRows}
              {renderedTotal}
            </table>
          </div>
        );
      })}
    </main>
  );
}
